import {
  createContext,
  useContext,
  useMemo,
  type FC,
  type ReactNode
} from "react";
import type { UIMessage } from "ai";

import {
  buildProgressInMessages,
  runEventsInMessages,
  runKey,
  type BuildProgress,
  type BuildProgressIndex,
  type RunEvent,
  type TrackedRun
} from "./runTracking";

const EMPTY = new Map<string, RunEvent>();
const NO_BUILDS: BuildProgressIndex = {
  byMessage: new Map(),
  planIds: new Set()
};

const NO_NAMES: ReadonlyMap<string, string> = new Map();

const RunEventsContext = createContext<Map<string, RunEvent>>(EMPTY);
const BuildProgressContext = createContext<BuildProgressIndex>(NO_BUILDS);
const FileNamesContext = createContext<ReadonlyMap<string, string>>(NO_NAMES);

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

/** How deep into a tool result the file-name scan looks. */
const NAME_SCAN_DEPTH = 4;

const textOf = (v: unknown): string | null =>
  typeof v === "string" && v ? v : null;

/**
 * File names by node id, read off the transcript's tool results (`{node_id,
 * name}` or `{kind: "file", id, name | title}` anywhere in an output): a file
 * saved or listed earlier in the conversation, named in a later row outside
 * the Case IDE's file tree.
 */
export const fileNamesInMessages = (
  messages: readonly UIMessage[]
): ReadonlyMap<string, string> => {
  const out = new Map<string, string>();
  const scan = (value: unknown, depth: number): void => {
    if (depth > NAME_SCAN_DEPTH) return;
    if (Array.isArray(value)) {
      value.forEach((v) => scan(v, depth + 1));
      return;
    }
    if (!isRecord(value)) return;
    const id =
      textOf(value.node_id) ??
      (value.kind === "file" ? textOf(value.id) : null);
    const name = textOf(value.name) ?? textOf(value.title);
    if (id && name) out.set(id, name);
    Object.values(value).forEach((v) => scan(v, depth + 1));
  };
  messages.forEach((message) =>
    message.parts.forEach((part) => {
      if (isRecord(part) && "output" in part) scan(part.output, 0);
    })
  );
  return out.size ? out : NO_NAMES;
};

type Props = {
  messages: readonly UIMessage[];
  children: ReactNode;
};

/**
 * Makes what a transcript says about itself available to the cards inside
 * it: the system notices about background runs (so the card that started a
 * run shows the notice's outcome before the tree or its own poll catches up)
 * and each plan build's latest todo state (so the Build card tracks progress
 * from the `update_todos` results that follow it), and the names of files
 * its tool results mention (so a later row names the file, not its id).
 */
export const RunEventsProvider: FC<Props> = ({ messages, children }) => {
  const events = useMemo(() => runEventsInMessages(messages), [messages]);
  const builds = useMemo(() => buildProgressInMessages(messages), [messages]);
  const names = useMemo(() => fileNamesInMessages(messages), [messages]);
  return (
    <RunEventsContext.Provider value={events}>
      <BuildProgressContext.Provider value={builds}>
        <FileNamesContext.Provider value={names}>
          {children}
        </FileNamesContext.Provider>
      </BuildProgressContext.Provider>
    </RunEventsContext.Provider>
  );
};

/** File names by node id from this transcript's tool results. */
export const useTranscriptFileNames = (): ReadonlyMap<string, string> =>
  useContext(FileNamesContext);

/** The latest system notice about `run`, or null when none has arrived. */
export const useRunEvent = (run: TrackedRun | null): RunEvent | null => {
  const events = useContext(RunEventsContext);
  return run ? events.get(runKey(run)) ?? null : null;
};

/**
 * The build the user message `messageId` started, at its latest todo state;
 * null outside a provider or when the message starts no build.
 */
export const useBuildProgress = (messageId: string): BuildProgress | null =>
  useContext(BuildProgressContext).byMessage.get(messageId) ?? null;

/** Whether a Build card for `planId` is somewhere in this transcript. */
export const useHasBuildCard = (planId: string | null): boolean => {
  const { planIds } = useContext(BuildProgressContext);
  return planId !== null && planIds.has(planId);
};
